import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import FormInput from '../components/FormInput'
import ProfileImageUpload from '../components/ProfileImageUpload'
import SubmitButton from '../components/SubmitButton'
import ImageCropModal, { ImageCropResult } from '../components/ImageCropModal'
import CustomSelect, { SelectOption } from '../components/CustomSelect'
import { userApi, UserProfile } from '../api/user'
import { authApi, ApiErrorResponse } from '../api'
import { toastUtil } from '../utils/toast'
import { useAuthStore } from '../stores/authStore'
import {
  acquireNoKeyboardResize,
  releaseNoKeyboardResize,
} from '../utils/flutterStatusBar'
import Gap from '../components/Gap'
import {
  getSidoOptions,
  getGuOptions,
  parseRegion,
  formatRegion,
} from '../constants/region'

/** 지역 드롭다운과 완료 버튼 사이에 유지할 간격(px) */
const REGION_MENU_GAP = 24

/** 목록이 목록답게 보이는 최소 높이(px). 한 항목이 약 44px */
const REGION_MENU_MIN = 44

const ProfileEdit: React.FC = () => {
  const navigate = useNavigate()
  const { setProfileImage: setStoreProfileImage } = useAuthStore()
  const [user, setUser] = useState<UserProfile | null>(null)
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(
    null
  )
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null)
  // 이미지 크롭 모달 (1:1)
  const [isCropOpen, setIsCropOpen] = useState(false)
  const [cropSource, setCropSource] = useState<string | null>(null)
  const [cropSourceName, setCropSourceName] = useState('profile.jpg')
  const [username, setUsername] = useState('')
  const [usernameVerified, setUsernameVerified] = useState(false)
  const [isCheckingUsername, setIsCheckingUsername] = useState(false)
  const [usernameError, setUsernameError] = useState('')
  const [nickname, setNickname] = useState('')
  const [nicknameError, setNicknameError] = useState('')
  const [phone, setPhone] = useState('') // 휴대폰 번호(수정 불가, 표시 전용)
  const [bio, setBio] = useState('')
  const [sido, setSido] = useState<string | null>(null)
  const [gu, setGu] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // 지역 드롭다운: 완료 버튼과 24px 간격을 유지하도록 열릴 때 가용 높이를 동적 계산
  const regionRowRef = useRef<HTMLDivElement>(null)
  const completeBtnRef = useRef<HTMLDivElement>(null)
  const usernameFieldRef = useRef<HTMLDivElement>(null)
  const [regionMenuMaxHeight, setRegionMenuMaxHeight] = useState(180)

  useEffect(() => {
    loadProfile()
  }, [])

  // [시험] 키보드 시 웹뷰 리사이즈 OFF → 입력칸 탭해도 하단 완료 버튼이 바닥 고정(안 밀림).
  // 화면 벗어나면 원복. (네이티브 브리지 — 신 앱에서만 동작, 구 앱은 무시)
  useEffect(() => {
    acquireNoKeyboardResize()
    return () => releaseNoKeyboardResize()
  }, [])

  const loadProfile = async () => {
    setIsLoading(true)
    try {
      const response = await userApi.getProfile()
      setUser(response.user)
      setUsername(response.user.username)
      setUsernameVerified(true) // 기존 아이디는 이미 확인된 것으로 간주
      setNickname(response.user.nickname)
      setPhone(response.user.phone || '')
      setBio(response.user.bio || '')
      setProfileImagePreview(response.user.profile_image || null)
      const parsedRegion = parseRegion(response.user.region)
      setSido(parsedRegion.sido)
      setGu(parsedRegion.gu)
    } catch (error) {
      console.error('프로필 정보 로드 실패:', error)
      // 에러는 apiClient의 interceptor에서 이미 처리됨
    } finally {
      setIsLoading(false)
    }
  }

  const handleProfileImageSelect = (file: File) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      // 크롭 모달 (1:1) 열기 — 결과는 onConfirm에서 적용
      setCropSource(reader.result as string)
      setCropSourceName(file.name || 'profile.jpg')
      setIsCropOpen(true)
    }
    reader.readAsDataURL(file)
  }

  // 크롭 완료 시 적용
  const handleCropConfirm = (result: ImageCropResult) => {
    setProfileImageFile(result.file)
    setProfileImagePreview(result.dataUrl)
    setIsCropOpen(false)
    setCropSource(null)
  }

  // 크롭 취소
  const handleCropCancel = () => {
    setIsCropOpen(false)
    setCropSource(null)
  }

  const handleUsernameCheck = async () => {
    if (!username) {
      setUsernameError('아이디를 입력해주세요')
      return
    }

    // 현재 아이디와 같으면 자동으로 확인 완료
    if (username === user?.username) {
      setUsernameVerified(true)
      setUsernameError('')
      toastUtil.info('현재 사용 중인 아이디입니다')
      return
    }

    if (usernameVerified && username === user?.username) {
      toastUtil.info('이미 확인 완료된 아이디입니다')
      return
    }

    if (isCheckingUsername) {
      toastUtil.info('이미 확인 중입니다')
      return
    }

    // 아이디 형식 검증
    if (username.length < 3 || username.length > 20) {
      setUsernameError('아이디는 3~20자로 입력해주세요')
      setUsernameVerified(false)
      return
    }

    if (!/^[a-zA-Z0-9._]+$/.test(username)) {
      setUsernameError('아이디는 영문, 숫자, 언더스코어(_)·마침표(.)만 사용 가능합니다')
      setUsernameVerified(false)
      return
    }

    if (/[._]$/.test(username)) {
      setUsernameError('아이디는 특수문자로 끝날 수 없습니다')
      setUsernameVerified(false)
      return
    }

    setIsCheckingUsername(true)
    setUsernameError('')
    try {
      const response = await authApi.checkUsername({ username })
      if (response.available) {
        setUsernameVerified(true)
        setUsernameError('')
        toastUtil.success('사용 가능한 아이디입니다')
      } else {
        setUsernameVerified(false)
        setUsernameError(response.message || '이미 존재하는 아이디입니다')
      }
    } catch (error: any) {
      setUsernameVerified(false)
      const apiError = error.response?.data as ApiErrorResponse
      if (apiError?.errors?.username) {
        setUsernameError(apiError.errors.username[0])
      } else {
        setUsernameError(
          apiError?.message || '아이디 확인 중 오류가 발생했습니다'
        )
      }
    } finally {
      setIsCheckingUsername(false)
    }
  }

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setUsername(value)
    setUsernameVerified(false) // 아이디 변경 시 확인 상태 초기화
    setUsernameError('')
  }

  const validateNickname = (value: string): boolean => {
    if (value.length < 2 || value.length > 15) {
      setNicknameError('2~15글자로 입력해주세요')
      return false
    }
    setNicknameError('')
    return true
  }

  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setNickname(value)
    if (value.length > 0) {
      validateNickname(value)
    } else {
      setNicknameError('')
    }
  }

  const handleSidoChange = (selected: SelectOption | null) => {
    setSido(selected?.value || null)
    setGu(null) // 시/도 변경 시 구 초기화
  }

  const handleGuChange = (selected: SelectOption | null) => {
    setGu(selected?.value || null)
  }

  /**
   * 지역 드롭다운 높이를 정한다.
   *
   * 목록은 위로 연다(menuPlacement="top"). 아래로 열면 화면 하단에 고정된
   * 완료 버튼과 닿는데, 기기가 작을수록 심해서 버튼을 덮기까지 했다.
   * 위로 열면 그 문제가 원천적으로 사라진다.
   *
   * 그래서 기준이 아래(완료 버튼)에서 위로 바뀐다. 화면 끝까지 올라가면
   * 사진·이름까지 가려 어느 화면인지 알 수 없으니, 아이디 입력칸 아래까지만 쓴다.
   *
   * 하한선을 두지 않는 이유
   * --------------------
   * 예전에는 Math.max(140, ...)으로 최소 140px을 보장했다. 남은 공간이 그보다
   * 좁아도 140px을 강제해 모자란 만큼 밖으로 넘쳤다. 목록이 짧아지면 스크롤하면
   * 되지만, 다른 요소를 가리면 조작 자체를 막는다.
   */
  const recomputeRegionMenuHeight = () => {
    const row = regionRowRef.current
    const topBound = usernameFieldRef.current
    if (!row) return

    // 아이디 칸 아래부터 지역 행 위까지가 위로 열 수 있는 공간
    const topEdge = topBound
      ? topBound.getBoundingClientRect().bottom
      : 0

    const avail = row.getBoundingClientRect().top - topEdge - REGION_MENU_GAP

    setRegionMenuMaxHeight(Math.max(REGION_MENU_MIN, Math.floor(avail)))
  }

  const handleSelectMenuOpen = () => {
    recomputeRegionMenuHeight()
    // 화면이 아직 움직이는 중일 수 있어 잠시 뒤 실제 위치로 다시 잰다
    setTimeout(recomputeRegionMenuHeight, 350)
  }

  const handleSaveProfile = async () => {
    // 아이디 검증
    if (!username) {
      setUsernameError('아이디를 입력해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return
    } else if (username.length < 3 || username.length > 20) {
      setUsernameError('아이디는 3~20자로 입력해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return
    } else if (!/^[a-zA-Z0-9._]+$/.test(username)) {
      setUsernameError('아이디는 영문, 숫자, 언더스코어(_)·마침표(.)만 사용 가능합니다')
      toastUtil.error('입력 값을 확인해주세요')
      return
    } else if (!usernameVerified && username !== user?.username) {
      setUsernameError('아이디 중복 확인을 해주세요')
      toastUtil.error('입력 값을 확인해주세요')
      return
    }

    if (!validateNickname(nickname)) {
      toastUtil.error('입력 값을 확인해주세요')
      return
    }

    setIsSaving(true)
    try {
      const regionString = formatRegion(sido, gu)
      const response = await userApi.updateProfile({
        username: username !== user?.username ? username : undefined,
        nickname,
        profile_image: profileImageFile || undefined,
        region: regionString || null,
        bio: bio !== user?.bio ? bio : undefined,
      })

      // 프로필 이미지 업데이트 후 store에 반영
      if (response.user.profile_image) {
        setStoreProfileImage(response.user.profile_image)
      } else {
        setStoreProfileImage(null)
      }

      toastUtil.success('프로필이 수정되었습니다')
      navigate(-1)
    } catch (error) {
      console.error('프로필 저장 실패:', error)
      // 에러는 apiClient의 interceptor에서 이미 처리됨
    } finally {
      setIsSaving(false)
    }
  }

  // 폼 검증 상태 계산
  const isFormValid = (() => {
    // 아이디 검증
    if (!username) return false
    if (username.length < 3 || username.length > 20) return false
    if (!/^[a-zA-Z0-9._]+$/.test(username)) return false
    if (!usernameVerified && username !== user?.username) return false

    // 닉네임 검증
    if (!nickname) return false
    if (nickname.length < 2 || nickname.length > 15) return false

    return true
  })()

  if (isLoading || !user) {
    return (
      <div className="w-full h-full flex flex-col">
        <AppBar title="프로필 수정" />
        <div className="app-container">
          <div className="flex-1 overflow-y-auto scrollbar-hide p-16px">
            <div className="w-full flex items-center justify-center py-48px">
              <div className="text-style-captionL text-textColorText3">
                프로필 정보를 불러오는 중...
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col">
      <AppBar title="프로필 수정" />

      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        <div className="flex-1 overflow-y-auto scrollbar-hide px-20px pb-24px">
          {/* 프로필 이미지 섹션 */}
          <ProfileImageUpload
            profileImagePreview={profileImagePreview}
            onFileSelect={handleProfileImageSelect}
          />

          <Gap height={24} />

          <form onSubmit={(e) => e.preventDefault()}>
            {/* 아이디 — 지역 드롭다운이 위로 열릴 때 여기까지만 올라온다 */}
            <div ref={usernameFieldRef}>
            <FormInput
              label="아이디"
              name="username"
              value={username}
              onChange={handleUsernameChange}
              placeholder="아이디를 입력해주세요"
              maxLength={20}
              buttonText={usernameVerified ? '확인완료' : '중복확인'}
              onButtonClick={handleUsernameCheck}
              buttonIsActive={!usernameVerified}
              error={usernameError}
            />
            </div>

            {/* 이름 */}
            <FormInput
              label="이름"
              name="name"
              value={nickname}
              onChange={handleNicknameChange}
              placeholder="이름을 입력해주세요"
              error={nicknameError}
              maxLength={15}
            />

            {/* 휴대폰 번호 (수정 불가 — 인증된 번호라 표시만) */}
            <FormInput
              label="휴대폰 번호"
              name="phone"
              type="tel"
              value={phone}
              onChange={() => {}}
              readOnly
            />

            {/* 지역 */}
            <div style={{ marginTop: '0px', marginBottom: '16px' }}>
              <label
                htmlFor="sido"
                className="block font-pretendard font-bold text-16px leading-20px text-brown-80%-main mb-10px"
              >
                지역
              </label>
              <div ref={regionRowRef} className="flex gap-12px">
                <div className="flex-1">
                  <CustomSelect
                    options={getSidoOptions()}
                    value={sido ? { value: sido, label: sido } : null}
                    onChange={handleSidoChange}
                    placeholder="시/도 선택"
                    backgroundColor="#FAF6EF"
                    boxShadow="none"
                    onMenuOpen={handleSelectMenuOpen}
                    menuPortal
                    menuPlacement="top"
                    maxMenuHeight={regionMenuMaxHeight}
                  />
                </div>
                {sido && (
                  <div className="flex-1">
                    <CustomSelect
                      options={getGuOptions(sido)}
                      value={gu ? { value: gu, label: gu } : null}
                      onChange={handleGuChange}
                      placeholder="구 선택"
                      isSearchable={false}
                      backgroundColor="#FAF6EF"
                      boxShadow="none"
                      onMenuOpen={handleSelectMenuOpen}
                      menuPortal
                      menuPlacement="top"
                      maxMenuHeight={regionMenuMaxHeight}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* 소개 */}
            <FormInput
              label="소개"
              name="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="소개를 입력해주세요"
              maxLength={200}
            />

          </form>
        </div>

        {/* 하단 고정 완료 버튼 (앱 표준 pb-32px) */}
        <div ref={completeBtnRef} className="w-full px-20px">
          <SubmitButton
            text="완료"
            onSubmit={handleSaveProfile}
            onDisabled={handleSaveProfile}
            disabled={!isFormValid || isSaving}
            loading={isSaving}
          />
        </div>
      </div>

      {/* 프로필 이미지 크롭 모달 (1:1) */}
      <ImageCropModal
        isOpen={isCropOpen}
        imageDataUrl={cropSource}
        aspect={1}
        fileName={cropSourceName}
        title="프로필 사진 편집"
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
      />
    </div>
  )
}

export default ProfileEdit
